import { ref, Ref } from 'vue';
import api from '@/services/api';
import { useStore } from 'vuex';

interface Product {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: string;
  stock: number;
  isActive: boolean;
  userId: string;
  mainPicture: string | null;
  category: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  renewedAt: string | null;
  followersCount: number;
  city: string;
  username: string;
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ProductsResponse {
  data: {
    data: Product[];
    meta: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

interface UserStats {
  activeProductsCount: number;
  totalProductsCount: number;
  maxProductsAllowed: number;
  remainingSlots: number;
  inactiveProductsCount: number;
}

interface Toast {
  show: boolean;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

export function useMyListings() {
  const store = useStore();
  const activeProducts = ref<Product[]>([]);
  const inactiveProducts = ref<Product[]>([]);
  const activeMeta = ref<Meta | null>(null);
  const inactiveMeta = ref<Meta | null>(null);

  // User stats state
  const userStats = ref<UserStats>({
    activeProductsCount: 0,
    totalProductsCount: 0,
    maxProductsAllowed: 30,
    remainingSlots: 0,
    inactiveProductsCount: 0,
  });
  const isLoadingStats = ref(false);

  const isLoadingActive = ref(false);
  const isLoadingInactive = ref(false);
  const errorActive = ref<string | null>(null);
  const errorInactive = ref<string | null>(null);

  // Toast notification state
  const toast = ref({
    show: false,
    message: '',
    type: 'info' as 'success' | 'error' | 'info' | 'warning',
  });

  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info'
  ) => {
    toast.value = {
      show: true,
      message,
      type,
    };

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      toast.value.show = false;
    }, 3000);
  };

  // Fetch user statistics
  const loadUserStats = async () => {
    isLoadingStats.value = true;
    try {
      const response = await api.get('/api/products/statistics');
      if (response.data && response.data.status === 'success') {
        userStats.value = response.data.data;
        console.log('User stats loaded:', userStats.value);
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
      // Fallback na default vrednosti ako ne može da učita
      userStats.value = {
        activeProductsCount: 0,
        totalProductsCount: 0,
        maxProductsAllowed: 30,
        remainingSlots: 30,
        inactiveProductsCount: 0,
      };
    } finally {
      isLoadingStats.value = false;
    }
  };

  // Fetch active products
  const fetchActiveProducts = async (page: number = 1) => {
    const userId = store.getters['auth/currentUser']?.id;
    const username = store.getters['auth/currentUser']?.username || '';
    if (!userId) {
      errorActive.value = 'User not authenticated';
      return;
    }

    isLoadingActive.value = true;
    errorActive.value = null;

    try {
      const response = await api.get<ProductsResponse>(
        `/api/products/user/${userId}?page=${page}`
      );

      // Add the missing properties required by ProductCard component
      activeProducts.value = response.data.data.data.map((product) => ({
        ...product,
        followersCount: product.followersCount || 0,
        city: product.city || 'Nepoznato',
        username: product.username || username,
      }));

      activeMeta.value = response.data.data.meta;
    } catch (error) {
      console.error('Error fetching active products:', error);
      errorActive.value = 'Failed to load active products';
    } finally {
      isLoadingActive.value = false;
    }
  };

  // Fetch inactive products
  const fetchInactiveProducts = async (page: number = 1) => {
    const userId = store.getters['auth/currentUser']?.id;
    const username = store.getters['auth/currentUser']?.username || '';
    if (!userId) {
      errorInactive.value = 'User not authenticated';
      return;
    }

    isLoadingInactive.value = true;
    errorInactive.value = null;

    try {
      const response = await api.get<ProductsResponse>(
        `/api/products/user/${userId}/inactive?page=${page}`
      );

      // Add the missing properties required by ProductCard component
      inactiveProducts.value = response.data.data.data.map((product) => ({
        ...product,
        followersCount: product.followersCount || 0,
        city: product.city || 'Nepoznato',
        username: product.username || username,
      }));

      inactiveMeta.value = response.data.data.meta;
    } catch (error) {
      console.error('Error fetching inactive products:', error);
      errorInactive.value = 'Failed to load inactive products';
    } finally {
      isLoadingInactive.value = false;
    }
  };

  // Delete product with pagination adjustment
  const deleteProductWithPagination = async (
    productId: string,
    isActive: boolean,
    currentPage: number,
    updatePageCallback: (newPage: number) => void
  ) => {
    try {
      await api.delete(`/api/products/${productId}`);

      // Remove product from the appropriate list
      if (isActive) {
        activeProducts.value = activeProducts.value.filter(
          (p) => p.id !== productId
        );
        // Update meta count
        if (activeMeta.value) {
          activeMeta.value.total -= 1;
        }
      } else {
        inactiveProducts.value = inactiveProducts.value.filter(
          (p) => p.id !== productId
        );
        // Update meta count
        if (inactiveMeta.value) {
          inactiveMeta.value.total -= 1;
        }
      }

      // Reload user stats after delete
      await loadUserStats();

      showToast('Proizvod je uspešno obrisan', 'success');

      // Check if we need to adjust pagination
      const currentProducts = isActive
        ? activeProducts.value
        : inactiveProducts.value;
      const currentMeta = isActive ? activeMeta.value : inactiveMeta.value;

      if (currentProducts.length === 0 && currentMeta && currentPage > 1) {
        // If current page is empty and we're not on page 1, go to previous page
        const newPage = Math.max(1, currentPage - 1);
        updatePageCallback(newPage);

        // Fetch the new page
        if (isActive) {
          await fetchActiveProducts(newPage);
        } else {
          await fetchInactiveProducts(newPage);
        }
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('Greška pri brisanju proizvoda', 'error');
    }
  };

  // Legacy delete product function for backward compatibility
  const deleteProduct = async (productId: string, isActive: boolean) => {
    try {
      await api.delete(`/api/products/${productId}`);

      // Remove product from the appropriate list
      if (isActive) {
        activeProducts.value = activeProducts.value.filter(
          (p) => p.id !== productId
        );
        // Update meta count
        if (activeMeta.value) {
          activeMeta.value.total -= 1;
        }
      } else {
        inactiveProducts.value = inactiveProducts.value.filter(
          (p) => p.id !== productId
        );
        // Update meta count
        if (inactiveMeta.value) {
          inactiveMeta.value.total -= 1;
        }
      }

      // Reload user stats after delete
      await loadUserStats();

      showToast('Proizvod je uspešno obrisan', 'success');
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('Greška pri brisanju proizvoda', 'error');
    }
  };

  // Renew inactive product (make it active again)
  const renewProduct = async (productId: string) => {
    // Check if user has remaining slots
    if (userStats.value.remainingSlots <= 0) {
      showToast(
        'Ne možete obnoviti oglas jer ste dostigli maksimalan broj aktivnih oglasa',
        'error'
      );
      return;
    }

    try {
      const response = await api.put(`/api/products/${productId}/renew`);

      // Move product from inactive to active list
      const renewedProduct = inactiveProducts.value.find(
        (p) => p.id === productId
      );
      if (renewedProduct) {
        // Update the product with the response data if available
        const updatedProduct = response.data.data || {
          ...renewedProduct,
          isActive: true,
        };

        // Remove from inactive list
        inactiveProducts.value = inactiveProducts.value.filter(
          (p) => p.id !== productId
        );

        // Update inactive meta count
        if (inactiveMeta.value) {
          inactiveMeta.value.total -= 1;
        }

        // Add to active list
        activeProducts.value.unshift(updatedProduct);

        // Update active meta count
        if (activeMeta.value) {
          activeMeta.value.total += 1;
        }
      }

      // Reload user stats after renew
      await loadUserStats();

      showToast('Proizvod je uspešno obnovljen', 'success');
    } catch (error) {
      console.error('Error renewing product:', error);
      showToast('Greška pri obnavljanju proizvoda', 'error');
    }
  };

  return {
    activeProducts,
    inactiveProducts,
    activeMeta,
    inactiveMeta,
    userStats,
    isLoadingStats,
    isLoadingActive,
    isLoadingInactive,
    errorActive,
    errorInactive,
    toast,
    fetchActiveProducts,
    fetchInactiveProducts,
    deleteProduct,
    deleteProductWithPagination,
    renewProduct,
    loadUserStats,
    showToast,
  };
}
